import { DialogProps } from "@mui/material";
import { useModalSelectors } from "../../../../app/state/modalSlice/hook/use-modal-selectors";
import { ModalBasic } from "../../../../shared/components/ModalBasic";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { DeerFlowInstancesAutocomplete } from "../DeerFlowInstancesAutocomplete";
import { DisplayedMessages } from "../../../../common/ChatComponents/DisplayedMessages";
import { MessageSendingInput } from "../../../../common/ChatComponents/MessageSendingInput";
import { SendMessageButton } from "../../../../common/ChatComponents/MessageSendingInput/Actions/SendMessageButton";
import { TModalsIds } from "../../../../app/state/modalSlice/types";
import styles from "./styles.module.css";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useEffect, useRef } from "react";
import {
  generateDeerFlowInstances,
  getDeerFlowInstanceAgents,
  getDeerFlowInstanceThreads,
} from "../../redux/actions";
import { DeerFlowAgentsSelect } from "../DeerFlowAgentsSelect";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import {
  selectDeerFlowMessageLoading,
  selectInstanceThreadMessages,
  selectInstanceThreads,
  selectSelectedDeerFlowInstance,
  selectSelectedInstanceAgent,
  selectSelectedInstanceThread,
} from "../../redux/selectors";
import { InstanceThreadsSelect } from "../InstanceThreadsSelect";
import { useDeerFlowChat } from "../../hooks/use-deer-flow-chat";
import { DeleteButton } from "../../../../common/Components/Buttons/DeleteButton";
import { AppIconButton } from "../../../../shared/components/AppIconButton";
import { TrashIcon } from "../../../../shared/icons/TrashIcon";
import { AppTooltip } from "../../../../shared/components/AppTooltip";

interface Props extends Omit<DialogProps, "open"> {
  closeModal: (id: TModalsIds) => void;
}

export const ChatWithAgentModal = ({ closeModal }: Props) => {
  const dispatch = useAppDispatch();
  const chatWithAgent = useModalSelectors("chat_with_agent_modal");
  const selectedInstance = useAppSelector(selectSelectedDeerFlowInstance);
  const selectedThread = useAppSelector(selectSelectedInstanceThread);
  const threads = useAppSelector(selectInstanceThreads);
  const selectedAgent = useAppSelector(selectSelectedInstanceAgent);
  const messages = useAppSelector(selectInstanceThreadMessages);
  const isMessageLoading = useAppSelector(selectDeerFlowMessageLoading);
  const {
    userMessage,
    handleChangeUserMessage,
    handleSendMessage,
    handleDeleteThread,
    handleSendKeyDown,
  } = useDeerFlowChat();
  const ref = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (selectedInstance) {
      dispatch(getDeerFlowInstanceAgents(selectedInstance.url));
      dispatch(getDeerFlowInstanceThreads(selectedInstance.url));
    }
  }, [selectedInstance]);

  useEffect(() => {
    if (chatWithAgent.isOpen) {
      dispatch(generateDeerFlowInstances());
    }
  }, [chatWithAgent.isOpen]);

  return (
    <ModalBasic
      open={chatWithAgent.isOpen}
      title={"Чат с Агентом"}
      closeModal={() => closeModal("chat_with_agent_modal")}
      onClose={() => closeModal("chat_with_agent_modal")}
      hasCloseButton
    >
      <div className={styles["modal-body"]}>
        <AppUnderlinedTitle leftContent={<DeerFlowInstancesAutocomplete />}>
          <div className={styles["select-container"]}>
            <DeerFlowAgentsSelect
              isDisabled={!selectedInstance || isMessageLoading}
            />
            <InstanceThreadsSelect
              isDisabled={!selectedInstance || isMessageLoading}
            />
          </div>
        </AppUnderlinedTitle>
        <DisplayedMessages
          messages={messages}
          isLoading={isMessageLoading}
          className={styles["chat"]}
        />
        <MessageSendingInput
          sendValue={userMessage}
          onChange={handleChangeUserMessage}
          handleKeyDown={handleSendKeyDown}
          isDisabled={!selectedThread && !selectedAgent}
          actions={
            <>
              <AppTooltip title={"Удалить выбранные тред"}>
                <AppIconButton
                  ref={ref}
                  buttonType={"outlined"}
                  onClick={handleDeleteThread}
                  disabled={!selectedThread}
                  className={styles["delete-btn"]}
                >
                  <TrashIcon />
                </AppIconButton>
              </AppTooltip>
              <SendMessageButton
                sendMessage={handleSendMessage}
                isDisabled={!selectedThread && !selectedAgent}
              />
            </>
          }
          rows={4}
          inputStyle={"elevated"}
        />
      </div>
    </ModalBasic>
  );
};
